import { useEffect, useState, type ReactNode } from "react";
import { startUnipileHostedAuth } from "../api/accounts.ts";
import { useToast } from "../components/ToastProvider.tsx";
import { useAppActions } from "../app/AppProvider.tsx";
import { channelOfAccount, setOnboardingActive, setSkippedOnboarding } from "../app/view.ts";

type Channel = { id: string; label: string; desc: string; color: string; icon: ReactNode };

const CHANNELS: Channel[] = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    desc: "Atenda e responda pelo número da empresa.",
    color: "#25D366",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.06L2 22l5.07-1.33A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.01.79.8-2.93-.2-.31A8.2 8.2 0 1 1 12 20.2zm4.5-6.14c-.25-.12-1.47-.72-1.7-.8-.22-.09-.39-.13-.55.12-.16.25-.63.8-.77.96-.14.16-.28.18-.53.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.48-1.38-1.73-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.55-1.34-.76-1.83-.2-.48-.4-.42-.55-.42h-.47c-.16 0-.42.06-.64.31-.22.25-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.6 4.12 3.64.58.25 1.03.4 1.38.51.58.18 1.1.16 1.52.1.46-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z" /></svg>
    ),
  },
  {
    id: "instagram",
    label: "Instagram",
    desc: "Receba e responda as DMs do seu perfil.",
    color: "#E1306C",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4.2" /><circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" /></svg>
    ),
  },
  {
    id: "email",
    label: "E-mail",
    desc: "Capture ordens de serviço que chegam por e-mail.",
    color: "#108b7c",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3.5 7l8.5 6 8.5-6" /></svg>
    ),
  },
];

export function Onboarding() {
  const { showToast } = useToast();
  const { state, dispatch } = useAppActions();
  const [step, setStep] = useState(0);
  const [linking, setLinking] = useState(false);

  // Marca que este usuário está no fluxo de onboarding (começou daqui), para que
  // o decideView o mantenha aqui enquanto conecta os canais.
  useEffect(() => {
    setOnboardingActive(true);
  }, []);

  const accounts = state.accounts || [];
  const connectedSet = new Set<string>(
    accounts.map((a) => channelOfAccount(a.accountType) || "").filter(Boolean),
  );
  const connectedCount = CHANNELS.filter((c) => connectedSet.has(c.id)).length;
  const allConnected = connectedCount >= CHANNELS.length;

  function skip() {
    setOnboardingActive(false);
    setSkippedOnboarding(true);
    dispatch({ type: "SET_VIEW", view: "crm" });
  }

  function goToCrm() {
    setOnboardingActive(false);
    dispatch({ type: "SET_VIEW", view: "crm" });
  }

  async function connect() {
    setLinking(true);
    try {
      const data = await startUnipileHostedAuth();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      showToast("Não foi possível abrir o pareamento. Tente novamente.", "error");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao abrir a conexão", "error");
    } finally {
      setLinking(false);
    }
  }

  const connectLabel = linking
    ? "Abrindo..."
    : connectedCount === 0
      ? "Conectar primeiro canal"
      : "Conectar mais um canal";

  return (
    <div className="hk-auth onb">
      <div className="hk-auth-form-side onb-side">
        <button type="button" className="onb-skip-top" onClick={skip}>
          Pular <i className="bi bi-arrow-right" />
        </button>

        <div className="onb-panel">
          <div className="onb-steps" aria-hidden="true">
            <span className={`onb-dot ${step === 0 ? "is-active" : "is-done"}`} />
            <span className={`onb-dot ${step === 1 ? "is-active" : ""}`} />
          </div>

          {step === 0 ? (
            <div className="onb-step">
              <span className="onb-eyebrow">Vamos começar</span>
              <h1>Em 1 minuto você está atendendo</h1>
              <p className="onb-sub">Veja o que vamos configurar para a sua operação:</p>

              <ul className="onb-check">
                <li>
                  <span className="onb-check-ic"><i className="bi bi-plug" /></span>
                  <span><strong>Conectar os 3 canais</strong> — WhatsApp, Instagram e e-mail</span>
                </li>
                <li>
                  <span className="onb-check-ic"><i className="bi bi-robot" /></span>
                  <span><strong>Ativar o assistente de IA</strong> — responde os contatos por você</span>
                </li>
                <li>
                  <span className="onb-check-ic"><i className="bi bi-clipboard-check" /></span>
                  <span><strong>Receber ordens de serviço</strong> — direto no seu funil</span>
                </li>
              </ul>

              <div className="onb-callout">
                <i className="bi bi-lightning-charge-fill" />
                <span>
                  <strong>Conecte os 3 canais.</strong> Quanto mais canais conectados, mais a IA atende
                  por você — e menos contatos e ordens de serviço escapam.
                </span>
              </div>

              <button type="button" className="hk-auth-btn" onClick={() => setStep(1)}>
                Começar
              </button>
              <button type="button" className="onb-skip" onClick={skip}>
                Pular por enquanto e ir para o CRM
              </button>
            </div>
          ) : (
            <div className="onb-step">
              <span className="onb-eyebrow">Passo final</span>
              <h1>Conecte seus 3 canais</h1>
              <p className="onb-sub">A IA atende em todos e nenhuma ordem de serviço escapa. Conecte um de cada vez.</p>

              <div className={`onb-progress ${allConnected ? "is-complete" : ""}`}>
                <div className="onb-progress-top">
                  <span className="onb-progress-count">
                    <strong>{connectedCount}</strong> de {CHANNELS.length} conectados
                  </span>
                  <span className="onb-progress-tag">
                    {allConnected ? "Tudo pronto! 🎉" : `Faltam ${CHANNELS.length - connectedCount}`}
                  </span>
                </div>
                <div className="onb-progress-bar">
                  <span style={{ width: `${(connectedCount / CHANNELS.length) * 100}%` }} />
                </div>
              </div>

              <div className="onb-channels">
                {CHANNELS.map((c) => {
                  const connected = connectedSet.has(c.id);
                  return (
                    <div key={c.id} className={`onb-channel ${connected ? "is-connected" : ""}`}>
                      <span className="onb-ch-ic" style={{ color: c.color }} aria-hidden="true">{c.icon}</span>
                      <span className="onb-ch-text">
                        <strong>{c.label}</strong>
                        <span>{connected ? "Conectado" : c.desc}</span>
                      </span>
                      <span className="onb-ch-check" aria-hidden="true">
                        <i className="bi bi-check-lg" />
                      </span>
                    </div>
                  );
                })}
              </div>

              {allConnected ? (
                <button type="button" className="hk-auth-btn" onClick={goToCrm}>
                  Ir para o CRM
                </button>
              ) : (
                <button type="button" className="hk-auth-btn" onClick={() => void connect()} disabled={linking}>
                  {connectLabel}
                </button>
              )}
              <p className="onb-secure">
                <i className="bi bi-shield-lock" />
                Conexão segura e criptografada
              </p>

              <div className="onb-foot">
                <button type="button" className="onb-link" onClick={() => setStep(0)}>
                  <i className="bi bi-arrow-left" /> Voltar
                </button>
                <button type="button" className="onb-link" onClick={skip}>
                  {connectedCount > 0 ? "Continuar depois" : "Pular por enquanto"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="hk-auth-showcase">
        <div className="hk-auth-showcase-inner">
          <div className="onb-brand">
            <span className="hk-brand-mark"><i className="bi bi-whatsapp" /></span>
            <span className="onb-brand-name">CRM</span>
          </div>
          <h2>Tudo da sua operação num só lugar.</h2>
          <ul className="hk-auth-feat">
            <li><i className="bi bi-chat-dots" />Atendimento unificado: WhatsApp, Instagram e e-mail</li>
            <li><i className="bi bi-robot" />Agentes de IA que respondem pelo seu negócio</li>
            <li><i className="bi bi-clipboard-check" />Ordens de serviço com notificação automática</li>
            <li><i className="bi bi-geo-alt" />A melhor rota, calculada pra você</li>
          </ul>
        </div>
        <div className="hk-auth-credit">Configuração rápida · leva 1 minuto</div>
      </div>
    </div>
  );
}
